/**
 * The two languages of §5.12, as vB6 has them.
 *
 * Hungarian is the page's language and English is a switch in the header.
 * `orders.lang` is `hu` only in release one (§19.3); the English on the page
 * exists because §5.12 asks every page string for both, and `hu-copy` checks
 * both halves of every pair.
 *
 * vB6 reads `S.lang` out of a module-level object from inside every helper.
 * Here the language is a value passed down, because a React tree that reads a
 * mutable global does not re-render when it changes — and because a string
 * helper that takes its language as an argument can be tested with both.
 */

export const LANGUAGES = ['hu', 'en'] as const
export type Lang = (typeof LANGUAGES)[number]

/** vB6's `T(hu, en)`, with the language given rather than looked up. */
export const pick = (lang: Lang, hu: string, en: string): string => (lang === 'hu' ? hu : en)

/** vB6's `L(o)`: a `{hu, en}` pair out of the catalogue or the register. */
export interface Bilingual {
  hu: string
  en: string
}

export const text = (lang: Lang, pair: Bilingual | null | undefined): string =>
  pair === null || pair === undefined ? '' : lang === 'hu' ? pair.hu : pair.en

/** A translator bound to one language, which is what a component is given. */
export interface Translate {
  lang: Lang
  (hu: string, en: string): string
  /** The `{hu, en}` form, for a name that came out of the database. */
  of(pair: Bilingual | null | undefined): string
}

export function translator(lang: Lang): Translate {
  const t = ((hu: string, en: string) => pick(lang, hu, en)) as Translate
  t.lang = lang
  t.of = (pair) => text(lang, pair)
  return t
}

export const otherLanguage = (lang: Lang): Lang => (lang === 'hu' ? 'en' : 'hu')
